<script setup>
import { ref, computed, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { DirectUpload } from 'activestorage';
import { setDirectUploadAuthHeaders } from 'dashboard/helper/directUploadsHelper';
import {
  checkFileSizeLimit,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';
import { messageStamp } from 'shared/helpers/timeHelper';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useEscapePriority } from 'dashboard/composables/useEscapePriority';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import scheduledMessagesApi from 'dashboard/api/inbox/scheduledMessages';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const EmojiIconPicker = defineAsyncComponent(
  () =>
    import('dashboard/components-next/emoji-icon-picker/EmojiIconPicker.vue')
);

const { t } = useI18n();

const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');

const dialogRef = ref(null);
const isLoadingList = ref(false);
const isSubmitting = ref(false);
const scheduledMessages = ref([]);

const content = ref('');
const scheduledAt = ref('');
const attachedFiles = ref([]);
const isUploadingFile = ref(false);
const fileInputRef = ref(null);
const showEmojiPicker = ref(false);

// `datetime-local` has no timezone info of its own — a minute of slack
// avoids the input's own `:min` rejecting "now" from a stale render.
const minScheduledAt = computed(() => {
  const now = new Date(Date.now() + 60 * 1000);
  now.setSeconds(0, 0);
  return now.toISOString().slice(0, 16);
});

const canSubmit = computed(() => {
  return (
    (content.value.trim().length > 0 || attachedFiles.value.length > 0) &&
    !!scheduledAt.value &&
    !isSubmitting.value &&
    !isUploadingFile.value
  );
});

const resetForm = () => {
  content.value = '';
  scheduledAt.value = '';
  attachedFiles.value = [];
};

const fetchScheduledMessages = async () => {
  isLoadingList.value = true;
  try {
    const response = await scheduledMessagesApi.get({
      conversationId: props.conversationId,
    });
    scheduledMessages.value = response.data;
  } catch (error) {
    useAlert(t('CONVERSATION.SCHEDULE_MESSAGE.API.FETCH_ERROR'));
  } finally {
    isLoadingList.value = false;
  }
};

// Dialog.vue's <dialog> already closes itself on Escape (native browser
// behavior) — this only tells ConversationHeader's global Escape handler to
// stand down, so Escape closes this dialog instead of also closing the
// ticket underneath it.
const isDialogOpen = ref(false);
useEscapePriority(isDialogOpen);

const open = () => {
  resetForm();
  dialogRef.value?.open();
  isDialogOpen.value = true;
  fetchScheduledMessages();
};

const handleDialogClose = () => {
  isDialogOpen.value = false;
};

defineExpose({ open });

const onSelectEmoji = ({ emoji }) => {
  content.value += emoji;
  showEmojiPicker.value = false;
};

const directUploadUrl = computed(
  () =>
    `/api/v1/accounts/${accountId.value}/conversations/${props.conversationId}/direct_uploads`
);

const uploadFile = file => {
  const maxSizeMB = resolveMaximumFileUploadSize(
    globalConfig.value.maximumFileUploadSize
  );

  if (!checkFileSizeLimit(file, maxSizeMB)) {
    useAlert(
      t('CONVERSATION.FILE_SIZE_LIMIT', {
        MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: maxSizeMB,
      })
    );
    return;
  }

  isUploadingFile.value = true;
  const upload = new DirectUpload(file, directUploadUrl.value, {
    directUploadWillCreateBlobWithXHR: xhr => setDirectUploadAuthHeaders(xhr),
  });

  upload.create((error, blob) => {
    isUploadingFile.value = false;
    if (error) {
      useAlert(error);
      return;
    }
    attachedFiles.value.push({
      id: blob.signed_id,
      blobSignedId: blob.signed_id,
      thumb: blob.content_type?.startsWith('image')
        ? URL.createObjectURL(file)
        : undefined,
      resource: {
        filename: blob.filename,
        content_type: blob.content_type,
        byte_size: blob.byte_size,
      },
    });
  });
};

const onFileInputChange = event => {
  Array.from(event.target.files || []).forEach(uploadFile);
  event.target.value = '';
};

const removeAttachment = updatedAttachments => {
  attachedFiles.value = updatedAttachments;
};

const onSubmit = async () => {
  if (!canSubmit.value) return;

  isSubmitting.value = true;
  try {
    await scheduledMessagesApi.create({
      conversationId: props.conversationId,
      content: content.value.trim(),
      scheduledAt: new Date(scheduledAt.value).toISOString(),
      files: attachedFiles.value.map(file => file.blobSignedId),
    });
    resetForm();
    useAlert(t('CONVERSATION.SCHEDULE_MESSAGE.API.CREATE_SUCCESS'));
    fetchScheduledMessages();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.SCHEDULE_MESSAGE.API.CREATE_ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};

const cancelScheduledMessage = async scheduledMessage => {
  try {
    await scheduledMessagesApi.delete(
      props.conversationId,
      scheduledMessage.id
    );
    scheduledMessages.value = scheduledMessages.value.filter(
      item => item.id !== scheduledMessage.id
    );
  } catch (error) {
    useAlert(t('CONVERSATION.SCHEDULE_MESSAGE.API.CANCEL_ERROR'));
  }
};

const formatScheduledAt = unixTimestamp =>
  messageStamp(unixTimestamp, 'MMM d, yyyy, HH:mm');
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CONVERSATION.SCHEDULE_MESSAGE.TITLE')"
    :description="t('CONVERSATION.SCHEDULE_MESSAGE.DESCRIPTION')"
    width="lg"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleDialogClose"
  >
    <div
      v-if="isLoadingList || scheduledMessages.length"
      class="flex flex-col gap-2 max-h-40 overflow-y-auto"
    >
      <div
        v-for="scheduledMessage in scheduledMessages"
        :key="scheduledMessage.id"
        class="flex items-start justify-between gap-3 p-3 rounded-lg bg-n-alpha-1"
      >
        <div class="flex flex-col min-w-0 gap-0.5">
          <p class="mb-0 text-sm truncate text-n-slate-12">
            {{
              scheduledMessage.content ||
              t('CONVERSATION.SCHEDULE_MESSAGE.ATTACHMENT_ONLY')
            }}
          </p>
          <span class="text-xs text-n-slate-11">
            {{
              t('CONVERSATION.SCHEDULE_MESSAGE.SCHEDULED_FOR', {
                dateTime: formatScheduledAt(scheduledMessage.scheduled_at),
              })
            }}
          </span>
        </div>
        <Button
          v-tooltip.top="t('CONVERSATION.SCHEDULE_MESSAGE.CANCEL_ITEM')"
          variant="ghost"
          color="ruby"
          size="sm"
          icon="i-lucide-trash-2"
          @click="cancelScheduledMessage(scheduledMessage)"
        />
      </div>
    </div>

    <div class="flex flex-col gap-3">
      <TextArea
        v-model="content"
        :placeholder="t('CONVERSATION.SCHEDULE_MESSAGE.PLACEHOLDER')"
        :max-length="MESSAGE_MAX_LENGTH.GENERAL"
        show-character-count
        min-height="8rem"
      />

      <AttachmentPreview
        v-if="attachedFiles.length"
        :attachments="attachedFiles"
        @remove-attachment="removeAttachment"
      />

      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="relative flex items-center gap-1">
          <Button
            v-tooltip.top="t('CONVERSATION.SCHEDULE_MESSAGE.ADD_EMOJI')"
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-ph-smiley-sticker"
            type="button"
            @click="showEmojiPicker = !showEmojiPicker"
          />
          <OnClickOutside @trigger="showEmojiPicker = false">
            <EmojiIconPicker
              v-if="showEmojiPicker"
              mode="emoji"
              class="bottom-full mb-2 ltr:start-0 rtl:end-0"
              @select="onSelectEmoji"
            />
          </OnClickOutside>
          <Button
            v-tooltip.top="t('CONVERSATION.SCHEDULE_MESSAGE.ADD_ATTACHMENT')"
            variant="ghost"
            color="slate"
            size="sm"
            :is-loading="isUploadingFile"
            icon="i-ph-paperclip"
            type="button"
            @click="fileInputRef?.click()"
          />
          <input
            ref="fileInputRef"
            type="file"
            multiple
            class="hidden"
            @change="onFileInputChange"
          />
        </div>
        <Input
          v-model="scheduledAt"
          type="datetime-local"
          :min="minScheduledAt"
          size="sm"
          class="w-full xs:w-auto xs:max-w-64"
        />
      </div>

      <div class="flex items-center justify-end gap-3">
        <Button
          variant="faded"
          color="slate"
          type="button"
          :label="t('CONVERSATION.SCHEDULE_MESSAGE.CANCEL')"
          @click="dialogRef?.close()"
        />
        <Button
          variant="solid"
          color="blue"
          type="button"
          :is-loading="isSubmitting"
          :disabled="!canSubmit"
          :label="t('CONVERSATION.SCHEDULE_MESSAGE.SCHEDULE')"
          @click="onSubmit"
        />
      </div>
    </div>
  </Dialog>
</template>
